import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/shared/Logo';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';
import { ShieldCheck, Loader2, ArrowLeft } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Sprint A — Per-assignment 5-checkbox consent gate (Ley 81).
 *
 * Renders full-screen above the assessment UI when the assignment has no
 * `consent.accepted_at` yet. On success, POSTs to
 * `/me/assignments/{aid}/consent` and unmounts (the parent re-fetches the
 * assignment so the gate disappears).
 *
 * Existing platform consent (BUG 20) remains required separately; this is an
 * additional per-assignment record (per founder Ley-81 compliance audit).
 */

const CHECKBOX_KEYS = [
  'voluntary_participation',
  'data_processing',
  'results_sharing_with_company',
  'retention_period',
  'rights_acknowledged',
];

export function AssignmentConsentGate({ assignmentId, onAccepted }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(() =>
    Object.fromEntries(CHECKBOX_KEYS.map((k) => [k, false])),
  );
  const [submitting, setSubmitting] = useState(false);

  const allChecked = CHECKBOX_KEYS.every((k) => checked[k]);
  const toggle = (k) => setChecked((p) => ({ ...p, [k]: !p[k] }));

  const submit = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...checked, language: i18n.language || 'es' };
      const { data } = await api.post(`/me/assignments/${assignmentId}/consent`, payload);
      toast.success(t('assignmentConsent.successToast', { defaultValue: 'Consent recorded' }));
      onAccepted?.(data);
    } catch (e) {
      toast.error(t('assignmentConsent.failed', { defaultValue: 'Could not record consent' }), {
        description: formatApiError(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gradient-subtle overflow-y-auto"
      data-testid="assignment-consent-gate"
    >
      <Card className="max-w-2xl w-full shadow-elegant p-7 lg:p-9 space-y-5 my-8">
        <div className="flex items-center justify-between gap-3">
          <Logo size="sm" />
          <LanguageSwitcher compact />
        </div>

        <div className="flex items-start gap-3">
          <div className="h-11 w-11 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">
              {t('assignmentConsent.title', { defaultValue: 'Informed consent for this evaluation' })}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              {t('assignmentConsent.subtitle', {
                defaultValue: 'Before you start, please review and accept the following statements. All checkboxes are mandatory.',
              })}
            </p>
          </div>
        </div>

        <div className="space-y-2.5">
          {CHECKBOX_KEYS.map((k) => (
            <label
              key={k}
              htmlFor={`assignment-consent-${k}`}
              className="flex items-start gap-3 rounded-xl border-2 border-border p-3.5 cursor-pointer hover:bg-muted/30 transition-colors"
            >
              <Checkbox
                id={`assignment-consent-${k}`}
                data-testid={`assignment-consent-${k}`}
                checked={!!checked[k]}
                onCheckedChange={() => toggle(k)}
              />
              <div className="flex-1 min-w-0 text-sm leading-relaxed">
                <Label htmlFor={`assignment-consent-${k}`} className="font-medium cursor-pointer">
                  {t(`assignmentConsent.cb.${k}.title`)}
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t(`assignmentConsent.cb.${k}.body`)}
                </p>
              </div>
            </label>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
          <p className="text-xs text-muted-foreground">
            {t('assignmentConsent.footnote', {
              defaultValue: 'Logged immutably in accordance with applicable data-protection legislation and the current privacy policy. You can request a copy from HR at any time.',
            })}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/me', { replace: true })}
              className="gap-1.5"
              data-testid="assignment-consent-back"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('assignmentConsent.backToDashboard', { defaultValue: 'Return to my dashboard' })}
            </Button>
            <Button
              onClick={submit}
              disabled={!allChecked || submitting}
              className="bg-gradient-primary gap-1.5"
              data-testid="assignment-consent-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {t('assignmentConsent.cta', { defaultValue: 'Start evaluation' })}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default AssignmentConsentGate;
